import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import bridgeService from '../../../services/bridgeService';
import { collectEngineersFromManagers } from '../../../utils/bridgeStaffDisplay';
import BridgeStaffReadonly from '../../../components/bridge/BridgeStaffReadonly';
import Loading from '../../../components/common/Loading';
import AppSelect from '../../../components/common/AppSelect';
import Modal from '../../../components/common/Modal';
import ConfirmModal from '../../../components/common/ConfirmModal';
import useNotify from '../../../hooks/useNotify';
import '../pages/DashboardPage.css';

const SENSOR_TYPES = [
  'Accelerometer',
  'Strain Gauge',
  'Deflection Gauge',
  'Camera',
  'Tiltmeter',
  'Pressure gauge',
];

const EMPTY_SENSOR_FORM = {
  sensorType: '',
  sensorSerialNo: '',
  spanNo: 1,
  girderNo: 1,
  manualLocation: '',
  dataLoggerId: '',
};

const isCamera = (sensorType) => sensorType === 'Camera';

const getLoggerDeviceCounts = (sensors, dataLoggerId) => {
  const id = String(dataLoggerId);
  const linked = sensors.filter((s) => s.dataLoggerId && String(s.dataLoggerId) === id);
  const cameras = linked.filter((s) => isCamera(s.sensorType)).length;
  const sensorCount = linked.length - cameras;
  return { sensorCount, cameras, total: linked.length };
};

const formatLoggerOptionLabel = (logger, sensors) => {
  const { sensorCount, cameras } = getLoggerDeviceCounts(sensors, logger._id);
  const parts = [];
  if (sensorCount) parts.push(`${sensorCount} sensor${sensorCount !== 1 ? 's' : ''}`);
  if (cameras) parts.push(`${cameras} camera${cameras !== 1 ? 's' : ''}`);
  const suffix = parts.length ? ` (${parts.join(', ')})` : '';
  return `${logger.serialNo}${suffix}`;
};

const countRange = (count) => {
  const n = Math.max(1, parseInt(count, 10) || 1);
  return Array.from({ length: n }, (_, i) => i + 1);
};

const clampToRange = (value, max) => Math.min(Math.max(1, value), Math.max(1, max));

const formatBridgeCoordinates = (coordinates) => {
  const lat = coordinates?.lat;
  const lng = coordinates?.lng;
  if (lat == null || lng == null || lat === '' || lng === '') return '—';
  return `${lat}, ${lng}`;
};

const getConsecutiveRowSpans = (items, getKey) => {
  const spans = new Array(items.length).fill(0);
  let i = 0;
  while (i < items.length) {
    const key = getKey(items[i]);
    let j = i + 1;
    while (j < items.length && getKey(items[j]) === key) j += 1;
    spans[i] = j - i;
    i = j;
  }
  return spans;
};

/** Normalize dataLoggerId whether it is a string, ObjectId, or populated object. */
const normalizeDataLoggerId = (dataLoggerId) => {
  if (dataLoggerId == null || dataLoggerId === '') return '';
  if (typeof dataLoggerId === 'object') {
    if (dataLoggerId._id != null && dataLoggerId._id !== '') {
      return String(dataLoggerId._id);
    }
    if (dataLoggerId.id != null && dataLoggerId.id !== '') {
      return String(dataLoggerId.id);
    }
    const asString = String(dataLoggerId);
    return asString === '[object Object]' ? '' : asString;
  }
  return String(dataLoggerId);
};

/** Stable group key so sensors of the same logger stay together after add or edit. */
const getDataLoggerGroupKey = (sensor, dataLoggers = []) => {
  const id = normalizeDataLoggerId(sensor?.dataLoggerId);
  if (!id) return '';
  const logger =
    dataLoggers.find((dl) => normalizeDataLoggerId(dl._id) === id)
    || dataLoggers.find((dl) => String(dl.serialNo) === id);
  if (logger?.serialNo) return `serial:${String(logger.serialNo)}`;
  return `id:${id}`;
};

const sensorTypeRank = (sensorType) => {
  const index = SENSOR_TYPES.indexOf(sensorType);
  return index === -1 ? SENSOR_TYPES.length : index;
};

/** Keep sensors for the same data logger together (by first-seen logger order). */
const consolidateSensorsByDataLogger = (sensors, dataLoggers = []) => {
  const groups = new Map();
  const order = [];
  sensors.forEach((sensor, index) => {
    const key = getDataLoggerGroupKey(sensor, dataLoggers);
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key).push({ sensor, index });
  });
  return order.flatMap((key) =>
    groups
      .get(key)
      .sort((a, b) => sensorTypeRank(a.sensor.sensorType) - sensorTypeRank(b.sensor.sensorType) || a.index - b.index)
      .map((item) => item.sensor)
  );
};

/**
 * Bridge details + sensor dashboard body.
 * - Dashboard page: pass `selected` + `onRefresh`
 * - Embedded (companies): pass `bridgeId` + `embedded`
 */
export default function BridgeDashboardView({
  bridgeId,
  selected: externalSelected,
  onRefresh,
  embedded = false,
  mapPanel = null,
  onGoToSensorData,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useNotify();

  const canEdit = ['bridge_manager', 'bridge_engineer', 'super_admin', 'project_manager', 'project_engineer'].includes(user?.role);
  const readOnly = ['bridge_owner', 'bridge_admin'].includes(user?.role);

  const [internalSelected, setInternalSelected] = useState(null);
  const [loading, setLoading] = useState(Boolean(embedded && bridgeId));
  const [modal, setModal] = useState(null);
  const [sensorForm, setSensorForm] = useState(EMPTY_SENSOR_FORM);
  const [newDataLoggerSerial, setNewDataLoggerSerial] = useState('');
  const [selectedDataLoggerId, setSelectedDataLoggerId] = useState('');
  const [dataLoggerLocation, setDataLoggerLocation] = useState('');
  const [numberOfGirders, setNumberOfGirders] = useState('5');
  const [numberOfBridgeSpans, setNumberOfBridgeSpans] = useState('1');
  const [saving, setSaving] = useState(false);
  const [addingLogger, setAddingLogger] = useState(false);
  const [sensorTypeFilter, setSensorTypeFilter] = useState('all');
  const [bridgeDetailsExpanded, setBridgeDetailsExpanded] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [loggerEdit, setLoggerEdit] = useState(null);
  const [loggerEditSerial, setLoggerEditSerial] = useState('');
  const [savingLogger, setSavingLogger] = useState(false);
  const [loggerDeleteTarget, setLoggerDeleteTarget] = useState(null);
  const [deletingLogger, setDeletingLogger] = useState(false);
  const prevBridgeIdRef = useRef(null);

  const selected = externalSelected || internalSelected;

  const loadEmbeddedBridge = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const full = await bridgeService.fetchDetail(id);
      setInternalSelected(full);
    } catch (e) {
      showToast(e.message, 'error');
      setInternalSelected(null);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!embedded || !bridgeId) return;
    setSensorTypeFilter('all');
    loadEmbeddedBridge(bridgeId);
  }, [bridgeId, embedded, loadEmbeddedBridge]);

  const refreshBridge = useCallback(async () => {
    const id = bridgeId || selected?._id;
    if (!id) return;
    if (onRefresh) {
      await onRefresh(id);
    } else {
      await loadEmbeddedBridge(id);
    }
  }, [bridgeId, selected?._id, onRefresh, loadEmbeddedBridge]);

  useEffect(() => {
    if (!selected?._id) return;
    const spans = Math.max(1, selected.numberOfSpans || 1);
    setNumberOfBridgeSpans(String(spans));

    if (prevBridgeIdRef.current !== selected._id) {
      prevBridgeIdRef.current = selected._id;
      setSensorTypeFilter('all');
      setSelectedDataLoggerId('');
      setNewDataLoggerSerial('');
      setDataLoggerLocation('');
      setSensorForm((prev) => ({
        ...prev,
        spanNo: clampToRange(prev.spanNo, spans),
        dataLoggerId: '',
      }));
      return;
    }

    setSensorForm((prev) => ({
      ...prev,
      spanNo: clampToRange(prev.spanNo, spans),
    }));
  }, [selected?._id, selected?.numberOfSpans]);

  useEffect(() => {
    if (sensorTypeFilter === 'all') return;
    const sensors = selected?.sensors || [];
    if (!sensors.some((sensor) => sensor.sensorType === sensorTypeFilter)) {
      setSensorTypeFilter('all');
    }
  }, [selected?.sensors, sensorTypeFilter]);

  const girderOptions = countRange(numberOfGirders);
  const spanOptions = countRange(numberOfBridgeSpans);

  const onNumberOfGirdersChange = (raw) => {
    if (raw !== '' && !/^\d+$/.test(raw)) return;
    setNumberOfGirders(raw);
    if (raw !== '') {
      const max = Math.max(1, parseInt(raw, 10));
      setSensorForm((prev) => ({ ...prev, girderNo: clampToRange(prev.girderNo, max) }));
    }
  };

  const onNumberOfGirdersBlur = () => {
    const max = Math.max(1, parseInt(numberOfGirders, 10) || 1);
    setNumberOfGirders(String(max));
    setSensorForm((prev) => ({ ...prev, girderNo: clampToRange(prev.girderNo, max) }));
  };

  const onNumberOfBridgeSpansChange = (raw) => {
    if (raw !== '' && !/^\d+$/.test(raw)) return;
    setNumberOfBridgeSpans(raw);
    if (raw !== '') {
      const max = Math.max(1, parseInt(raw, 10));
      setSensorForm((prev) => ({ ...prev, spanNo: clampToRange(prev.spanNo, max) }));
    }
  };

  const onNumberOfBridgeSpansBlur = () => {
    const max = Math.max(1, parseInt(numberOfBridgeSpans, 10) || 1);
    setNumberOfBridgeSpans(String(max));
    setSensorForm((prev) => ({ ...prev, spanNo: clampToRange(prev.spanNo, max) }));
  };

  if (loading) return <Loading message="Loading bridge dashboard..." />;
  if (!selected) {
    return (
      <div className="card" style={{ padding: 20, color: '#888' }}>
        Bridge details unavailable.
      </div>
    );
  }

  const allSensors = selected.sensors || [];
  const dataLoggers = (selected.dataLoggers || []).filter((dl) => dl.isActive !== false);

  const resolveDataLoggerLabel = (dataLoggerId) => {
    const id = normalizeDataLoggerId(dataLoggerId);
    if (!id) return '—';
    const logger =
      dataLoggers.find((dl) => normalizeDataLoggerId(dl._id) === id)
      || dataLoggers.find((dl) => String(dl.serialNo) === id);
    return logger?.serialNo || '—';
  };

  const resolveDataLoggerLocation = (dataLoggerId) => {
    const id = normalizeDataLoggerId(dataLoggerId);
    if (!id) return '—';
    const logger =
      dataLoggers.find((dl) => normalizeDataLoggerId(dl._id) === id)
      || dataLoggers.find((dl) => String(dl.serialNo) === id);
    return logger?.location?.trim() || '—';
  };

  const persistDataLoggerLocation = async (dataLoggerId = selectedDataLoggerId, location = dataLoggerLocation) => {
    if (!dataLoggerId) return;
    const trimmed = location.trim();
    const logger = dataLoggers.find((dl) => String(dl._id) === String(dataLoggerId));
    if ((logger?.location || '') === trimmed) return;
    await bridgeService.updateDataLogger(selected._id, dataLoggerId, { location: trimmed });
    if (externalSelected && onRefresh) {
      await onRefresh(selected._id);
    } else {
      setInternalSelected((prev) => (prev ? {
        ...prev,
        dataLoggers: (prev.dataLoggers || []).map((dl) =>
          String(dl._id) === String(dataLoggerId) ? { ...dl, location: trimmed } : dl
        ),
      } : prev));
    }
  };

  const handleAddDataLogger = async () => {
    const serialNo = newDataLoggerSerial.trim();
    if (!serialNo) return showToast('Enter a data logger serial number.', 'error');
    setAddingLogger(true);
    try {
      const res = await bridgeService.addDataLogger(selected._id, {
        serialNo,
        name: '',
        location: '',
      });
      const created = res.dataLogger;
      showToast(`Data logger "${serialNo}" added.`);
      setNewDataLoggerSerial('');
      if (created?._id) {
        setSelectedDataLoggerId(created._id);
        setDataLoggerLocation('');
        setSensorForm((prev) => ({ ...prev, dataLoggerId: created._id }));
      }
      await refreshBridge();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setAddingLogger(false);
    }
  };

  const onSelectDataLogger = (dataLoggerId) => {
    setSelectedDataLoggerId(dataLoggerId);
    const logger = dataLoggers.find((dl) => String(dl._id) === String(dataLoggerId));
    setDataLoggerLocation(dataLoggerId ? (logger?.location || '') : '');
    setSensorForm((prev) => ({ ...prev, dataLoggerId }));
  };

  const openEditDataLogger = (logger) => {
    setLoggerEdit(logger);
    setLoggerEditSerial(logger?.serialNo || '');
  };

  const handleSaveDataLogger = async () => {
    if (!loggerEdit?._id || savingLogger) return;
    const serialNo = loggerEditSerial.trim();
    if (!serialNo) return showToast('Enter a data logger id.', 'error');
    const duplicate = dataLoggers.some(
      (dl) => String(dl._id) !== String(loggerEdit._id)
        && String(dl.serialNo).trim().toLowerCase() === serialNo.toLowerCase()
    );
    if (duplicate) return showToast('A data logger with this id already exists.', 'error');
    if (serialNo === String(loggerEdit.serialNo || '').trim()) {
      setLoggerEdit(null);
      return;
    }
    setSavingLogger(true);
    try {
      await bridgeService.updateDataLogger(selected._id, loggerEdit._id, {
        serialNo,
        name: loggerEdit.name || '',
        location: loggerEdit.location || '',
      });
      showToast('Data logger updated.');
      setLoggerEdit(null);
      await refreshBridge();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSavingLogger(false);
    }
  };

  const confirmDeleteDataLogger = async () => {
    if (!loggerDeleteTarget?._id || !selected?._id || deletingLogger) return;
    setDeletingLogger(true);
    try {
      const loggerId = normalizeDataLoggerId(loggerDeleteTarget._id);
      const loggerSerial = String(loggerDeleteTarget.serialNo || '').trim();
      const linkedSensors = allSensors.filter((sensor) => {
        const linkedId = normalizeDataLoggerId(sensor?.dataLoggerId);
        if (!linkedId) return false;
        return linkedId === loggerId || (loggerSerial !== '' && linkedId === loggerSerial);
      });
      for (const sensor of linkedSensors) {
        if (!sensor?._id) continue;
        await bridgeService.deleteSensor(selected._id, sensor._id);
      }
      await bridgeService.deleteDataLogger(selected._id, loggerDeleteTarget._id);
      if (String(selectedDataLoggerId) === String(loggerDeleteTarget._id)) {
        setSelectedDataLoggerId('');
        setDataLoggerLocation('');
      }
      setSensorForm((prev) => {
        const linkedId = normalizeDataLoggerId(prev.dataLoggerId);
        if (linkedId !== loggerId && linkedId !== loggerSerial) return prev;
        return { ...prev, dataLoggerId: '' };
      });
      showToast('Data logger removed.');
      setLoggerDeleteTarget(null);
      await refreshBridge();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setDeletingLogger(false);
    }
  };

  const onDataLoggerLocationBlur = async () => {
    if (!selectedDataLoggerId) return;
    try {
      await persistDataLoggerLocation();
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const addedSensorTypes = SENSOR_TYPES.filter((type) =>
    allSensors.some((sensor) => sensor.sensorType === type)
  );
  const extraSensorTypes = [...new Set(
    allSensors
      .map((sensor) => sensor.sensorType)
      .filter((type) => type && !SENSOR_TYPES.includes(type))
  )];
  const sensorTypeOptions = ['all', ...addedSensorTypes, ...extraSensorTypes];

  const filteredSensors = consolidateSensorsByDataLogger(
    sensorTypeFilter === 'all'
      ? allSensors
      : allSensors.filter((s) => s.sensorType === sensorTypeFilter),
    dataLoggers
  );

  const dataLoggerRowSpans = getConsecutiveRowSpans(
    filteredSensors,
    (s) => getDataLoggerGroupKey(s, dataLoggers)
  );

  const handleAddSensor = async () => {
    if (!sensorForm.sensorType || !sensorForm.sensorSerialNo.trim()) {
      return showToast('Sensor Type and Serial No. are required.', 'error');
    }
    setSaving(true);
    try {
      const payload = {
        ...sensorForm,
        dataLoggerId: sensorForm.dataLoggerId || undefined,
      };
      if (sensorForm.dataLoggerId) {
        await persistDataLoggerLocation(sensorForm.dataLoggerId);
      }
      await bridgeService.addSensor(selected._id, payload);
      showToast('Sensor added!');
      setModal(null);
      setSensorForm({
        ...EMPTY_SENSOR_FORM,
        spanNo: sensorForm.spanNo,
        girderNo: sensorForm.girderNo,
        dataLoggerId: selectedDataLoggerId,
      });
      await refreshBridge();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSensor = async () => {
    setSaving(true);
    try {
      const dataLoggerId = normalizeDataLoggerId(sensorForm.dataLoggerId) || null;
      await bridgeService.updateSensor(selected._id, modal.sensor._id, {
        ...sensorForm,
        dataLoggerId,
      });
      showToast('Sensor updated.');
      setModal(null);
      await refreshBridge();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSensor = (sensor) => {
    if (!sensor?._id || deleting) return;
    setDeleteTarget(sensor);
  };

  const confirmDeleteSensor = async () => {
    if (!deleteTarget?._id || !selected?._id || deleting) return;
    setDeleting(true);
    try {
      await bridgeService.deleteSensor(selected._id, deleteTarget._id);
      showToast('Sensor removed.');
      setDeleteTarget(null);
      await refreshBridge();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  const openEdit = (s) => {
    setSensorForm({
      sensorType: s.sensorType,
      sensorSerialNo: s.sensorSerialNo,
      spanNo: s.spanNo,
      girderNo: s.girderNo,
      manualLocation: s.manualLocation || '',
      dataLoggerId: normalizeDataLoggerId(s.dataLoggerId),
    });
    setModal({ type: 'edit', sensor: s });
  };

  const goToSensorData = () => {
    if (typeof onGoToSensorData === 'function') {
      onGoToSensorData(selected._id);
      return;
    }
    if (embedded) {
      navigate('/sensor-data', { state: { bridgeId: selected._id } });
    } else {
      window.location.href = '/sensor-data';
    }
  };

  const dashboardMain = (
    <>
      {readOnly && (
        <div className="access-note">
          <i className="ti ti-eye" /> <b>View only:</b> You can view sensor details but cannot add, edit or delete sensors.
        </div>
      )}

      <div className={`dashboard-bridge-details${bridgeDetailsExpanded ? '' : ' is-collapsed'}`}>
        <button
          type="button"
          className="dashboard-bridge-details-title"
          onClick={() => setBridgeDetailsExpanded((prev) => !prev)}
          aria-expanded={bridgeDetailsExpanded}
        >
          <span className="dashboard-bridge-details-title-label">
            <i className="ti ti-info-circle" /> Bridge details — {selected.name}
          </span>
          <i
            className={`ti ti-chevron-down dashboard-bridge-details-toggle${bridgeDetailsExpanded ? ' is-expanded' : ''}`}
            aria-hidden="true"
          />
        </button>
        {bridgeDetailsExpanded && (
          <>
            <div className="dashboard-location-grid">
              {[
                ['Country', selected.country],
                ['State', selected.state],
                ['City', selected.city],
                ['Division', selected.division],
                ['Bridge Coordinates', formatBridgeCoordinates(selected.coordinates)],
                ['ZIP Code', selected.zipCode],
              ].map(([label, value]) => (
                <div key={label} className="dashboard-location-item">
                  <label>{label}</label>
                  <span>{value || '—'}</span>
                </div>
              ))}
            </div>
            <div className="dashboard-staff-grid">
              <BridgeStaffReadonly label="Bridge Admin" people={selected.admins} />
              <BridgeStaffReadonly label="Bridge Manager" people={selected.managers} />
              <BridgeStaffReadonly
                label="Bridge Engineer"
                people={collectEngineersFromManagers(selected.managers)}
              />
              <BridgeStaffReadonly label="Bridge Owner" people={selected.owners} />
            </div>
          </>
        )}
      </div>

      {user?.role !== 'bridge_engineer' && (
      <div className="dashboard-form-card">
        <div className="dashboard-card-header">
          <i className="ti ti-layout-dashboard" /> Dashboard — {selected.name}
        </div>

        {canEdit && (
          <div className="dashboard-sensor-form">
            <div className="dashboard-form-row dashboard-form-row--top">
              <div className="form-group">
                <label>Number of Bridge Spans</label>
                <input
                  type="number"
                  min="1"
                  value={numberOfBridgeSpans}
                  onChange={(e) => onNumberOfBridgeSpansChange(e.target.value)}
                  onBlur={onNumberOfBridgeSpansBlur}
                />
              </div>
              <div className="form-group">
                <label>Span No.</label>
                <AppSelect
                  value={sensorForm.spanNo}
                  onChange={(value) => setSensorForm((p) => ({ ...p, spanNo: parseInt(value, 10) }))}
                  options={spanOptions.map((x) => ({ value: x, label: String(x) }))}
                />
              </div>
              <div className="form-group">
                <label>Number of Girders</label>
                <input
                  type="number"
                  min="1"
                  value={numberOfGirders}
                  onChange={(e) => onNumberOfGirdersChange(e.target.value)}
                  onBlur={onNumberOfGirdersBlur}
                />
              </div>
              <div className="form-group">
                <label>Girder No.</label>
                <AppSelect
                  value={sensorForm.girderNo}
                  onChange={(value) => setSensorForm((p) => ({ ...p, girderNo: parseInt(value, 10) }))}
                  options={girderOptions.map((x) => ({ value: x, label: String(x) }))}
                />
              </div>
              <div className="form-group form-group-add-logger">
                <label>Add Data Logger</label>
                <input
                  type="text"
                  placeholder="e.g. DL_ATAL_001"
                  value={newDataLoggerSerial}
                  onChange={(e) => setNewDataLoggerSerial(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddDataLogger()}
                />
              </div>
              <button
                type="button"
                className="dashboard-data-logger-add-btn dashboard-form-row-top-btn"
                onClick={handleAddDataLogger}
                disabled={addingLogger}
              >
                {addingLogger ? '...' : 'Add'}
              </button>
            </div>

            {dataLoggers.length > 0 && (
              <div className="dashboard-form-row dashboard-form-row--data-logger">
                <div className="form-group form-group-data-logger-select">
                  <label>Select Data Logger</label>
                  <AppSelect
                    value={selectedDataLoggerId}
                    onChange={onSelectDataLogger}
                    options={[
                      { value: '', label: 'None (optional)' },
                      ...dataLoggers.map((dl) => ({
                        value: dl._id,
                        label: formatLoggerOptionLabel(dl, allSensors),
                        actions: [
                          {
                            key: 'edit',
                            icon: 'ti-pencil',
                            label: 'Edit data logger',
                            onClick: () => openEditDataLogger(dl),
                          },
                          {
                            key: 'delete',
                            icon: 'ti-trash',
                            label: 'Delete data logger',
                            danger: true,
                            onClick: () => setLoggerDeleteTarget(dl),
                          },
                        ],
                      })),
                    ]}
                  />
                  <div className="dashboard-data-logger-hint">
                    Optional: link sensors/cameras to logger
                  </div>
                </div>
                <div className="form-group form-group-data-logger-location">
                  <label>Data Logger Location</label>
                  <input
                    type="text"
                    placeholder={selectedDataLoggerId ? 'e.g. Span 1 - Pier A' : 'Select a Data Logger first'}
                    value={dataLoggerLocation}
                    disabled={!selectedDataLoggerId}
                    onChange={(e) => setDataLoggerLocation(e.target.value)}
                    onBlur={onDataLoggerLocationBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && selectedDataLoggerId) {
                        e.preventDefault();
                        onDataLoggerLocationBlur();
                      }
                    }}
                  />
                </div>
              </div>
            )}

            <div className="dashboard-form-row dashboard-form-row--sensor">
              <div className="form-group">
                <label>Sensor Type</label>
                <AppSelect
                  value={sensorForm.sensorType}
                  onChange={(value) => setSensorForm((p) => ({ ...p, sensorType: value }))}
                  placeholder="Select Sensor Type"
                  options={[
                    { value: '', label: 'Select Sensor Type' },
                    ...SENSOR_TYPES.map((t) => ({ value: t, label: t })),
                  ]}
                />
              </div>
              <div className="form-group form-group-bridge-sensor-sr">
                <label title="Bridge Sensor Sr. No.">Sensor Sr. No.</label>
                <input
                  type="text"
                  placeholder="e.g. ACC_ATAL_001"
                  value={sensorForm.sensorSerialNo}
                  onChange={(e) => setSensorForm((p) => ({ ...p, sensorSerialNo: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Sensor Location</label>
                <input
                  type="text"
                  placeholder="e.g. Top Bottom - Span 1"
                  value={sensorForm.manualLocation}
                  onChange={(e) => setSensorForm((p) => ({ ...p, manualLocation: e.target.value }))}
                />
              </div>
            </div>

            <div className="dashboard-form-actions">
              <button type="button" className="dashboard-sensor-add-btn" onClick={handleAddSensor}>
                Add
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </>
  );

  return (
    <div className={embedded ? 'bridge-dashboard-embedded' : 'dashboard-view'}>
      {mapPanel ? (
        <div className="bridge-dashboard-embedded-split">
          <div className="bridge-dashboard-embedded-main">{dashboardMain}</div>
          <div className="bridge-dashboard-embedded-map">{mapPanel}</div>
        </div>
      ) : (
        dashboardMain
      )}

      <div className="dashboard-sensors-card">
        <div className="dashboard-sensor-toolbar">
          <div className="dashboard-sensor-toolbar-filters">
            <label>Sensor type</label>
            <AppSelect
              value={sensorTypeFilter}
              closedLabel={allSensors.length === 0 ? 'All' : undefined}
              onChange={(value) => {
                if (allSensors.length === 0 || value === 'none') return;
                setSensorTypeFilter(value);
              }}
              options={allSensors.length === 0
                ? [{ value: 'none', label: 'No sensors available' }]
                : sensorTypeOptions.map((t) => ({
                  value: t,
                  label: t === 'all' ? 'All' : t,
                }))}
            />
          </div>
          <span className="dashboard-sensor-count">
            Showing {filteredSensors.length} of {allSensors.length} sensor{allSensors.length !== 1 ? 's' : ''}
          </span>
        </div>

        <table className="dashboard-sensors-table">
          <thead>
            <tr>
              <th className="col-index">Sr.no.</th>
              <th>Span no.</th>
              <th>Girder no.</th>
              <th>Data Logger</th>
              <th>Data Logger Location</th>
              <th>Sensor type</th>
              <th>Bridge sensor sr. no.</th>
              <th>Sensor location</th>
              {canEdit && <><th>Edit</th><th>Delete</th></>}
            </tr>
          </thead>
          <tbody>
            {allSensors.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 10 : 8} className="dashboard-sensors-empty">
                  {readOnly
                    ? 'No sensors added yet.'
                    : 'No sensors added yet. Use the form above to add sensors.'}
                </td>
              </tr>
            ) : filteredSensors.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 10 : 8} className="dashboard-sensors-empty">
                  No sensors match the selected type.
                </td>
              </tr>
            ) : filteredSensors.map((s, i) => (
              <tr key={s._id}>
                <td className="col-index">{String(i + 1).padStart(2, '0')}</td>
                <td>{s.spanNo}</td>
                <td>{s.girderNo}</td>
                {dataLoggerRowSpans[i] > 0 && (
                  <td rowSpan={dataLoggerRowSpans[i]} className="dashboard-data-logger-merged">
                    {resolveDataLoggerLabel(s.dataLoggerId)}
                  </td>
                )}
                <td>{resolveDataLoggerLocation(s.dataLoggerId)}</td>
                <td>{s.sensorType}</td>
                <td>{s.sensorSerialNo}</td>
                <td>{s.manualLocation || '—'}</td>
                {canEdit && (
                  <>
                    <td>
                      <button
                        type="button"
                        className="dashboard-sensor-edit-btn"
                        onClick={() => openEdit(s)}
                      >
                        <i className="ti ti-pencil" />
                      </button>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="dashboard-sensor-delete-btn"
                        onClick={() => handleDeleteSensor(s)}
                      >
                        <i className="ti ti-trash" />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="dashboard-next-actions">
        <button type="button" className="dashboard-next-btn" onClick={goToSensorData}>
          Next: Sensor data <i className="ti ti-chevron-right" />
        </button>
      </div>

      {modal?.type === 'edit' && (
        <Modal title="Edit Sensor" onClose={() => setModal(null)} width={500}>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label>Sensor Type</label>
            <AppSelect
              value={sensorForm.sensorType}
              onChange={(value) => setSensorForm((p) => ({ ...p, sensorType: value }))}
              options={SENSOR_TYPES.map((t) => ({ value: t, label: t }))}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label>Bridge Sensor Sr. No.</label>
            <input
              type="text"
              value={sensorForm.sensorSerialNo}
              onChange={(e) => setSensorForm((p) => ({ ...p, sensorSerialNo: e.target.value }))}
            />
          </div>
          {dataLoggers.length > 0 && (
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label>Data Logger</label>
              <AppSelect
                value={sensorForm.dataLoggerId}
                onChange={(value) => setSensorForm((p) => ({ ...p, dataLoggerId: value }))}
                options={[
                  { value: '', label: 'None (optional)' },
                  ...dataLoggers.map((dl) => ({
                    value: dl._id,
                    label: formatLoggerOptionLabel(dl, allSensors),
                  })),
                ]}
              />
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label>Span No.</label>
              <AppSelect
                value={sensorForm.spanNo}
                onChange={(value) => setSensorForm((p) => ({ ...p, spanNo: parseInt(value, 10) }))}
                options={spanOptions.map((x) => ({ value: x, label: String(x) }))}
              />
            </div>
            <div className="form-group">
              <label>Girder No.</label>
              <AppSelect
                value={sensorForm.girderNo}
                onChange={(value) => setSensorForm((p) => ({ ...p, girderNo: parseInt(value, 10) }))}
                options={girderOptions.map((x) => ({ value: x, label: String(x) }))}
              />
            </div>
            <div className="form-group">
              <label>Location</label>
              <input
                type="text"
                value={sensorForm.manualLocation}
                onChange={(e) => setSensorForm((p) => ({ ...p, manualLocation: e.target.value }))}
              />
            </div>
          </div>
          <button
            type="button"
            className="btn btn-blue"
            style={{ width: '100%', padding: 8, marginTop: 12 }}
            onClick={handleEditSensor}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Sensor"
          message={
            <>
              Delete sensor{' '}
              <strong>{deleteTarget.sensorSerialNo || deleteTarget.sensorType || 'this sensor'}</strong>
              ? This cannot be undone.
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          loading={deleting}
          onConfirm={confirmDeleteSensor}
          onCancel={() => !deleting && setDeleteTarget(null)}
        />
      )}

      {loggerEdit && (
        <Modal title="Edit Data Logger" onClose={() => !savingLogger && setLoggerEdit(null)} width={460}>
          <p className="confirm-modal-message">
            You are editing {loggerEdit.serialNo || loggerEdit.name || loggerEdit._id}.
          </p>
          <div className="form-group" style={{ marginBottom: 10 }}>
            <label>Data Logger ID</label>
            <input
              type="text"
              value={loggerEditSerial}
              onChange={(e) => setLoggerEditSerial(e.target.value)}
              disabled={savingLogger}
            />
          </div>
          <button
            type="button"
            className="btn btn-blue"
            style={{ width: '100%', padding: 8, marginTop: 12 }}
            onClick={handleSaveDataLogger}
            disabled={savingLogger}
          >
            {savingLogger ? 'Saving...' : 'Save Changes'}
          </button>
        </Modal>
      )}

      {loggerDeleteTarget && (
        <ConfirmModal
          title="Delete Data Logger"
          message={
            <>
              Delete data logger{' '}
              <strong>{loggerDeleteTarget.serialNo || loggerDeleteTarget._id}</strong>
              ? Sensors linked to it will also be deleted. This cannot be undone.
            </>
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          loading={deletingLogger}
          onConfirm={confirmDeleteDataLogger}
          onCancel={() => !deletingLogger && setLoggerDeleteTarget(null)}
        />
      )}
    </div>
  );
}
